import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import Icons from './Icons';
import toast from 'react-hot-toast';
import { downloadMultiShotZip } from '../services/api';
import { downloadFile } from '../utils/downloadFile';

// ── Data retention config per plan ──
const RETENTION_MONTHS = {
  free: 1,
  basic: 1,
  go: 2,
  plus: 4,
};
const GRACE_DAYS = 7;

const MULTISHOT_STYLE_LABELS = {
  cinematic1: [
    'Medium Close-Up',
    'Two-Shot Mirror',
    'High Angle Overhead',
    'Extreme Wide Night',
    "Worm's Eye",
    'Close-Up Profile',
    'Rack Focus Foreground',
    'Slow Motion Freeze',
    'Over-Shoulder Reverse',
  ],
  cinematic2: [
    'Steadicam Follow',
    'Extreme Close-Up Hands',
    'Aerial Crane',
    'Dutch Angle Close',
    'Point of View',
    'Silhouette Interior',
    'Telephoto Compression',
    'Low Angle Environment',
    'Insert Detail',
  ],
  aesthetic1: [
    'Golden Hour Wide',
    'Puddle Reflection',
    'Window Light Portrait',
    'Through Foliage',
    'Overhead Flat Lay',
    'Lens Flare Close-Up',
    'Golden Telephoto',
    'Leading Lines Wide',
    'Doorway Silhouette',
  ],
  normalShots1: [
    'Establishing Wide',
    'Shoulder Medium',
    'Cowboy Shot',
    'Clean Close-Up',
    'Full Body Neutral',
    'Walking Side',
    'Over-Shoulder Clean',
    'Two-Shot Equal',
    'Ground Upward Tilt',
  ],
  gameShots1: [
    'Third-Person Cam',
    'First-Person View',
    'Isometric High Angle',
    'Side Scroll Flat',
    'Boss Reveal Low',
    'Tactical Overhead',
    'Cutscene Close',
    'Environment Wide',
    'Defeat Slow Motion',
  ],
  actionShots1: [
    'Running Lateral',
    'Impact Close',
    'Aerial Pursuit',
    'Ground Charge',
    'Dutch Chaos',
    'Freeze Frame',
    'Heroic Sprint',
    'Action POV',
    'Wide Chaos',
  ],
  techShots1: [
    'Hero Product Wide',
    'Macro Circuit',
    'Overhead Workspace',
    'Screen Glow Portrait',
    'Glass Reflection',
    'Interface Close-Up',
    'Device Monument',
    'DOF Stack',
    'Dark Studio',
  ],
  animalShots1: [
    'Wildlife Wide',
    'Extreme Eye Close-Up',
    'Ground Eye Level',
    'Aerial Tracking',
    'Profile Telephoto',
    'Motion Blur Pursuit',
    'Golden Silhouette',
    'Behavior Medium',
    'Environmental Portrait',
  ],
};

function getMultiShotJobId(item) {
  if (item?.jobId) return item.jobId;
  if (typeof item?.id === 'string' && item.id.startsWith('multishot_')) {
    return item.id.replace(/^multishot_/, '');
  }
  return '';
}

function getMultiShotShotLabel(item, panel, idx) {
  const styleLabels = MULTISHOT_STYLE_LABELS[item?.style] || [];
  return styleLabels[idx] || panel.label || `Shot ${String(idx + 1).padStart(2, '0')}`;
}

function getRetentionInfo(plan, createdAt) {
  if (!createdAt) return null;

  // Parse Firestore timestamp
  let ts;
  if (createdAt.seconds || createdAt._seconds) {
    ts = (createdAt.seconds || createdAt._seconds) * 1000;
  } else {
    ts = new Date(createdAt).getTime();
  }
  if (!ts || isNaN(ts)) return null;

  const created = new Date(ts);
  const months = RETENTION_MONTHS[plan] || 1;
  const expiry = new Date(created);
  expiry.setMonth(expiry.getMonth() + months);
  const graceExpiry = new Date(expiry);
  graceExpiry.setDate(graceExpiry.getDate() + GRACE_DAYS);

  const now = new Date();
  const daysLeft = Math.ceil((graceExpiry - now) / (1000 * 60 * 60 * 24));

  return {
    months,
    expiryDate: expiry,
    graceDate: graceExpiry,
    daysLeft,
    isExpired: daysLeft <= 0,
    isInGrace: now >= expiry && now < graceExpiry,
    planName: plan.charAt(0).toUpperCase() + plan.slice(1),
  };
}

export default function Lightbox({ item, onClose, onDelete }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { plan } = useCredits();
  const [fullscreen, setFullscreen] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [naturalRes, setNaturalRes] = useState(null);
  const imgRef = useRef(null);
  const vidRef = useRef(null);

  // Reset resolution when item changes
  useEffect(() => {
    setNaturalRes(null);
  }, [item]);

  // Escape key: close fullscreen first, then close lightbox
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (fullscreen) setFullscreen(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen, onClose]);

  if (!item) return null;

  const url = item.imageUrl || item.videoUrl;
  const isMultiShot = item.type === 'multishot';
  const isImage = item.type !== 'video';
  const multiShotJobId = getMultiShotJobId(item);

  let sourceMedia = null;
  try {
    if (item.promptOutput) sourceMedia = JSON.parse(item.promptOutput);
  } catch(e) {}
  const multishotPanels = Array.isArray(item.multishotPanels) ? item.multishotPanels.filter(panel => panel.url) : [];

  const retention = getRetentionInfo(plan, item.createdAt);

  // ── Navigation helpers ──
  const goEdit = (mode) => {
    onClose();
    if (mode === 'recreate') {
      navigate('/generate', { state: { sourceUrl: url, prompt: item.prompt, action: 'recreate' } });
    } else if (mode === 'upscale') {
      navigate('/upscale', { state: { sourceUrl: url, prompt: item.prompt, action: 'upscale' } });
    } else {
      navigate('/edit', { state: { sourceUrl: url, prompt: item.prompt, mode, fromLightbox: true } });
    }
  };

  const goImageToVideo = () => {
    onClose();
    navigate('/frame2video', { state: { sourceUrl: url, action: 'image-to-video' } });
  };

  const goUseAsReference = () => {
    onClose();
    navigate('/frame2video', { state: { sourceUrl: url, action: 'use-as-reference' } });
  };

  const goAngles = () => {
    onClose();
    navigate('/angles', { state: { sourceUrl: url } });
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Link copied!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      if (isMultiShot && multiShotJobId) {
        await downloadMultiShotZip(token, multiShotJobId);
        toast.success('Download started!');
        return;
      }
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${API_URL}/download?url=${encodeURIComponent(url)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const ext = isImage ? 'png' : 'mp4';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `aifield_${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('Download started!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleShotDownload = async (e, panel, idx) => {
    e.stopPropagation();
    try {
      await downloadFile(panel.url, token);
      toast.success(`${getMultiShotShotLabel(item, panel, idx)} downloaded`);
    } catch (err) {
      console.error('Shot download error:', err);
      toast.error('Shot download failed');
    }
  };

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (isMultiShot && multishotPanels.length > 0) {
      setFullscreen({ url: multishotPanels[0].url, type: 'image' });
      return;
    }
    setFullscreen({ url, type: isImage ? 'image' : 'video' });
  };

  // ── Dynamic resolution detection ──
  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setNaturalRes(`${img.naturalWidth}×${img.naturalHeight}`);
    }
  };

  const handleVideoMeta = (e) => {
    const vid = e.target;
    if (vid.videoWidth && vid.videoHeight) {
      setNaturalRes(`${vid.videoWidth}×${vid.videoHeight}`);
    }
  };

  // Display resolution: prefer dynamic, fallback to stored
  const displayRes = naturalRes || item.resolution || null;

  // ── Fullscreen overlay (rendered as overlay — no unmount flash) ──
  const renderFullscreen = () => {
    if (!fullscreen) return null;
    
    const fsUrl = typeof fullscreen === 'string' ? fullscreen : fullscreen.url;
    let fsType = 'image';
    if (typeof fullscreen === 'object' && fullscreen.type) {
      fsType = fullscreen.type;
    } else if (typeof fullscreen === 'string') {
      if (fsUrl.endsWith('.mp4') || fsUrl.includes('.mp4?')) {
        fsType = 'video';
      } else if (fsUrl.endsWith('.mp3') || fsUrl.includes('.mp3?') || fsUrl.endsWith('.wav') || fsUrl.includes('.wav?')) {
        fsType = 'audio';
      }
    }

    return (
      <div
        className="img-fs"
        onClick={() => setFullscreen(null)}
        style={{ zIndex: 80 }}
      >
        {fsType === 'video' ? (
          <video
            key={fsUrl}
            src={fsUrl}
            controls
            autoPlay
            loop
            className="img-fs-img"
            onClick={e => e.stopPropagation()}
            style={{ background: 'transparent' }}
          />
        ) : fsType === 'audio' ? (
          <div 
            onClick={e => e.stopPropagation()} 
            style={{ background: 'rgba(15,15,25,0.95)', padding: '60px', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
          >
            <Icons.Audio sz={80} style={{ color: 'var(--t2)', opacity: 0.8 }} />
            <audio
              key={fsUrl}
              src={fsUrl}
              controls
              autoPlay
              style={{ width: '400px', maxWidth: '90vw' }}
            />
          </div>
        ) : (
          <img
            key={fsUrl}
            src={fsUrl}
            alt=""
            className="img-fs-img"
            onClick={e => e.stopPropagation()}
          />
        )}
        <button
          className="img-fs-close"
          onClick={(e) => { e.stopPropagation(); setFullscreen(null); }}
          title="Close (Esc)"
        >
          <Icons.X sz={16} />
        </button>
      </div>
    );
  };

  const btnStyle = {
    background: 'rgba(15,15,25,0.75)',
    border: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(8px)',
  };

  return (
    <>
    {renderFullscreen()}
    <div className="lb" onClick={onClose}>
      {/* ── Central content ── */}
      <div className="lb-container" onClick={e => e.stopPropagation()}>

        {/* ── Media + Toolbar Row ── */}
        <div className="lb-media-row">
          {/* Image / Video — properly constrained */}
          <div className="lb-main">
            {isMultiShot ? (
              <div className="lb-multishot-grid">
                {multishotPanels.map((panel, idx) => {
                  const shotLabel = getMultiShotShotLabel(item, panel, idx);
                  return (
                  <div
                    key={panel.index}
                    className="lb-multishot-shot"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFullscreen({ url: panel.url, type: 'image' });
                    }}
                    title={shotLabel}
                  >
                    <img src={panel.url} alt={shotLabel} />
                    <button
                      className="lb-shot-download"
                      onClick={(e) => handleShotDownload(e, panel, idx)}
                      title="Download this shot"
                    >
                      <Icons.DL sz={13} />
                    </button>
                    <div className="lb-shot-label">
                      <strong>{String(idx + 1).padStart(2, '0')}</strong>
                      <span>{shotLabel}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : !isImage ? (
              <video
                ref={vidRef}
                src={url}
                controls
                autoPlay
                loop
                className="lb-img"
                onLoadedMetadata={handleVideoMeta}
              />
            ) : (
              <img
                ref={imgRef}
                src={url}
                alt=""
                className="lb-img"
                onLoad={handleImageLoad}
              />
            )}
          </div>

          {/* Right vertical toolbar */}
          <div className="lb-toolbar">
            <button className="prev-icon-btn" onClick={(e) => { e.stopPropagation(); onClose(); }} title="Close" style={btnStyle}>
              <Icons.X sz={15} />
            </button>
            <button className="prev-icon-btn" onClick={toggleFullscreen} title="Fullscreen" style={btnStyle}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
              </svg>
            </button>
            {isImage && !isMultiShot && (
              <button className="prev-icon-btn" onClick={(e) => { e.stopPropagation(); goEdit('recreate'); }} title="Edit / Recreate" style={btnStyle}>
                <Icons.Edit sz={15} />
              </button>
            )}
            <button className="prev-icon-btn" onClick={handleDownload} title={downloading ? 'Downloading...' : (isMultiShot ? 'Download all shots (ZIP)' : 'Download')} style={{ ...btnStyle, opacity: downloading ? 0.5 : 1 }}>
              <Icons.DL sz={15} />
            </button>
            <button className="prev-icon-btn" onClick={handleCopy} title="Copy link" style={btnStyle}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
            {onDelete && (
              <button className="prev-icon-btn del" onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} title="Delete" style={btnStyle}>
                <Icons.Trash sz={15} />
              </button>
            )}
          </div>
        </div>

        {/* ── Bottom action bar (images only) ── */}
        {isImage && !isMultiShot && (
          <div className="lb-actions-bar">
            <button className="prev-act" onClick={(e) => { e.stopPropagation(); goUseAsReference(); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              Use as Reference
            </button>
            <button className="prev-act" onClick={(e) => { e.stopPropagation(); goImageToVideo(); }}>
              <Icons.Vid sz={14} />
              Image to Video
            </button>
            <button className="prev-act" onClick={(e) => { e.stopPropagation(); goEdit('recreate'); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 4v6h6M23 20v-6h-6"/>
                <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"/>
              </svg>
              Recreate
            </button>
            <button className="prev-act" onClick={(e) => { e.stopPropagation(); goEdit('upscale'); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35M11 8v6M8 11h6"/>
              </svg>
              Upscale
            </button>
            <button className="prev-act" onClick={(e) => { e.stopPropagation(); goAngles(); }}>
              <span style={{ fontSize: '13px' }}>🔄</span>
              Angles
            </button>
          </div>
        )}

        {/* ── Info panel ── */}
        <div className="lb-info-panel">

          {/* ── Source Media ── */}
          {sourceMedia && (sourceMedia.startFrameUrl || sourceMedia.endFrameUrl || sourceMedia.elementUrls?.length > 0 || sourceMedia.referenceUrls?.length > 0) && (
            <div className="lb-section">
              <span className="lb-section-label">Source Media</span>
              <div className="lb-source-thumbs">
                {sourceMedia.startFrameUrl && (
                  <div className="lb-source-thumb" onClick={(e) => { e.stopPropagation(); setFullscreen({ url: sourceMedia.startFrameUrl, type: 'image' }); }}>
                    <img src={sourceMedia.startFrameUrl} alt="Start Frame" />
                    <span className="lb-source-tag">Start</span>
                  </div>
                )}
                {sourceMedia.endFrameUrl && (
                  <div className="lb-source-thumb" onClick={(e) => { e.stopPropagation(); setFullscreen({ url: sourceMedia.endFrameUrl, type: 'image' }); }}>
                    <img src={sourceMedia.endFrameUrl} alt="End Frame" />
                    <span className="lb-source-tag">End</span>
                  </div>
                )}
                {sourceMedia.elementUrls?.map((u, i) => (
                  <div key={`el-${i}`} className="lb-source-thumb" onClick={(e) => { e.stopPropagation(); setFullscreen({ url: u, type: 'image' }); }}>
                    <img src={u} alt={`Element ${i+1}`} />
                    <span className="lb-source-tag">Elem</span>
                  </div>
                ))}
                {sourceMedia.referenceUrls?.map((ref, i) => {
                  const u = typeof ref === 'string' ? ref : ref.url;
                  const t = typeof ref === 'string' ? 'image' : ref.type;
                  return (
                    <div key={`ref-${i}`} className="lb-source-thumb" onClick={(e) => { 
                      e.stopPropagation(); 
                      setFullscreen({ url: u, type: t }); 
                    }}>
                      {t === 'image' ? (
                        <img src={u} alt={`Reference ${i+1}`} />
                      ) : t === 'video' ? (
                        <video src={u} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                      ) : (
                        <div 
                          style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.1)' }}
                        >
                          <Icons.Audio sz={20} />
                        </div>
                      )}
                      <span className="lb-source-tag">Ref</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Prompt ── */}
          {isMultiShot && item.sourceUrl && (
            <div className="lb-section">
              <span className="lb-section-label">Original Upload</span>
              <div className="lb-source-thumbs">
                <div className="lb-source-thumb" onClick={(e) => { e.stopPropagation(); setFullscreen({ url: item.sourceUrl, type: 'image' }); }}>
                  <img src={item.sourceUrl} alt="Original upload" />
                  <span className="lb-source-tag">Source</span>
                </div>
                {item.gridUrl && (
                  <div className="lb-source-thumb" onClick={(e) => { e.stopPropagation(); setFullscreen({ url: item.gridUrl, type: 'image' }); }}>
                    <img src={item.gridUrl} alt="Storyboard grid" />
                    <span className="lb-source-tag">Grid</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {item.prompt && (
            <div className="lb-section">
              <span className="lb-section-label">Prompt</span>
              <p className="lb-prompt-text">
                {item.prompt.length > 200 ? item.prompt.slice(0, 200) + '…' : item.prompt}
              </p>
            </div>
          )}

          {/* ── Metadata chips ── */}
          <div className="lb-meta-grid">
            <div className="lb-meta-chip">
              <span className="lb-chip-label">Model</span>
              <span className="lb-chip-value">{item.modelName || item.model}</span>
            </div>
            <div className="lb-meta-chip">
              <span className="lb-chip-label">Credits</span>
              <span className="lb-chip-value">{item.creditsUsed} cr</span>
            </div>
            {displayRes && (
              <div className="lb-meta-chip">
                <span className="lb-chip-label">Resolution</span>
                <span className="lb-chip-value">{displayRes}</span>
              </div>
            )}
            {item.generationTime && (
              <div className="lb-meta-chip">
                <span className="lb-chip-label">Gen Time</span>
                <span className="lb-chip-value">{item.generationTime}s</span>
              </div>
            )}
            {item.aspectRatio && (
              <div className="lb-meta-chip">
                <span className="lb-chip-label">Aspect</span>
                <span className="lb-chip-value">{item.aspectRatio}</span>
              </div>
            )}
            {item.type && (
              <div className="lb-meta-chip">
                <span className="lb-chip-label">Type</span>
                <span className="lb-chip-value" style={{ textTransform: 'capitalize' }}>{isMultiShot ? 'MultiShot' : item.type}</span>
              </div>
            )}
            {isMultiShot && (
              <div className="lb-meta-chip">
                <span className="lb-chip-label">Shots</span>
                <span className="lb-chip-value">{multishotPanels.length}/9</span>
              </div>
            )}
          </div>

          {/* ── Data Retention Info ── */}
          {retention && (
            <div className={`lb-retention ${retention.isExpired ? 'expired' : retention.isInGrace ? 'grace' : 'active'}`}>
              <div className="lb-retention-icon">
                {retention.isExpired ? '⚠️' : retention.isInGrace ? '⏳' : '🛡️'}
              </div>
              <div className="lb-retention-info">
                {retention.isExpired ? (
                  <>
                    <span className="lb-retention-title">Data Expired</span>
                    <span className="lb-retention-desc">This file has exceeded the {retention.months}-month retention for your {retention.planName} plan. It may be deleted soon.</span>
                  </>
                ) : retention.isInGrace ? (
                  <>
                    <span className="lb-retention-title">Grace Period — {retention.daysLeft} days left</span>
                    <span className="lb-retention-desc">Your {retention.months}-month retention has ended. Renew your {retention.planName} plan within {retention.daysLeft} days to keep this file.</span>
                  </>
                ) : (
                  <>
                    <span className="lb-retention-title">Retained until {retention.expiryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span className="lb-retention-desc">{retention.planName} plan — {retention.months} month{retention.months > 1 ? 's' : ''} retention + {GRACE_DAYS}-day grace period</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
